import { useEffect, useState } from 'react';
import { Clock, MapPin, Plus, Trash2 } from 'lucide-react';
import { useAuthedApi } from '../../lib/useAuthedApi';

type Rule = {
  id?: string;
  day_of_week: number;
  // 0 = every week, 1 = Week A, 2 = Week B.
  week: number;
  start_time: string;
  end_time: string;
  // null = these hours apply at every location the staff member works at.
  location_id: string | null;
};

// Shape returned by GET /staff/:id/locations (locations row + assignment flags).
type Loc = {
  id: string;
  name: string;
  timezone: string;
  hours_json: string;
  is_virtual: number;
  is_active: number;
  is_default: number;
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Key order matches locations.hours_json and DAY_NAMES (0 = Sunday).
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const WEEK_OPTIONS = [
  { value: 0, label: 'Every week' },
  { value: 1, label: 'Week A' },
  { value: 2, label: 'Week B' },
];

// Matches PutSchema's cap in src/api/staff/availability.ts — checked here too
// so an over-cap save names the problem instead of returning an opaque 400.
const MAX_RULES = 128;

// Mirrors weekParity() in src/lib/availability.ts: 2023-01-01 is a Sunday, so
// parity 0 = Week A, 1 = Week B. Kept in sync with the backend.
const WEEK_EPOCH_MS = Date.UTC(2023, 0, 1);
function weekParity(d: Date): 0 | 1 {
  const days = Math.floor((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - WEEK_EPOCH_MS) / 86_400_000);
  return (((Math.floor(days / 7) % 2) + 2) % 2) as 0 | 1;
}

// The Sunday..Saturday range containing `d`, formatted "Jun 29 – Jul 5".
function weekRangeLabel(d: Date): string {
  const sunday = new Date(d);
  sunday.setDate(d.getDate() - d.getDay());
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  const fmt = (x: Date) => x.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(sunday)} – ${fmt(saturday)}`;
}

/**
 * Compact "Mon–Fri 09:00–18:00 · Sat 10:00–14:00" summary of the office's own
 * opening hours, with consecutive matching days collapsed into a run.
 *
 * Worth showing next to the editor because location hours gate staff
 * availability (generateSlots requires both): hours set outside the office's
 * open windows silently produce no bookable slots.
 */
function summarizeHours(loc: Loc): string {
  if (loc.is_virtual) return 'Online — open any time, so your hours below are the only limit.';

  let hours: Record<string, { open: string; close: string }[]>;
  try {
    hours = JSON.parse(loc.hours_json || '{}');
  } catch {
    return '';
  }

  const perDay = DAY_KEYS.map((k) => (hours[k] ?? []).map((w) => `${w.open}–${w.close}`).join(', '));
  const runs: { from: number; to: number; text: string }[] = [];
  perDay.forEach((text, i) => {
    const last = runs[runs.length - 1];
    if (last && last.text === text) last.to = i;
    else runs.push({ from: i, to: i, text });
  });

  const parts = runs
    .filter((r) => r.text)
    .map((r) =>
      r.from === r.to
        ? `${DAY_NAMES[r.from]} ${r.text}`
        : `${DAY_NAMES[r.from]}–${DAY_NAMES[r.to]} ${r.text}`,
    );
  return parts.length
    ? `Office open ${parts.join(' · ')} — hours outside these windows won't be offered.`
    : 'This office has no opening hours set, so no slots will be offered.';
}

export default function Availability() {
  const apiFn = useAuthedApi();
  const [staffId, setStaffId] = useState<string | null>(null);
  const [staffTz, setStaffTz] = useState<string>('');
  const [rules, setRules] = useState<Rule[]>([]);
  const [locations, setLocations] = useState<Loc[]>([]);
  const [perLocation, setPerLocation] = useState(false);
  const [activeLocId, setActiveLocId] = useState<string | null>(null);
  const [biweekly, setBiweekly] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    apiFn<{ id: string; timezone: string }>('/staff/me')
      .then(async (me) => {
        const [rs, locs] = await Promise.all([
          apiFn<Rule[]>(`/staff/${me.id}/availability`),
          apiFn<Loc[]>(`/staff/${me.id}/locations`),
        ]);
        const active = locs.filter((l) => l.is_active);
        const assigned = new Set(active.map((l) => l.id));
        setStaffId(me.id);
        setStaffTz(me.timezone);
        setLocations(active);
        setRules(rs);
        // Show the A/B UI if any existing rule uses a rotation.
        setBiweekly(rs.some((r) => r.week !== 0));
        // Per-location mode is inferred from the data: a row pinned to a
        // location this person actually works at. Rows left pinned to a
        // location they're no longer assigned to are inert (every slot query
        // joins staff_locations first) and deliberately don't flip the mode.
        setPerLocation(rs.some((r) => r.location_id && assigned.has(r.location_id)));
        setActiveLocId((active.find((l) => l.is_default) ?? active[0])?.id ?? null);
      })
      .catch((e) => setErr(e.message));
  }, [apiFn]);

  const activeLoc = locations.find((l) => l.id === activeLocId) ?? null;

  // Indices into `rules` that the current view owns. Anything else — rows
  // pinned to a location without a tab — is preserved untouched through save.
  const visibleIdx = rules
    .map((_r, i) => i)
    .filter((i) =>
      perLocation ? rules[i]!.location_id === activeLocId : rules[i]!.location_id === null,
    );

  function addRule() {
    setRules([
      ...rules,
      {
        day_of_week: 1,
        week: biweekly ? 1 : 0,
        start_time: '09:00',
        end_time: '17:00',
        location_id: perLocation ? activeLocId : null,
      },
    ]);
  }
  function updateRule(idx: number, patch: Partial<Rule>) {
    setRules(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removeRule(idx: number) {
    setRules(rules.filter((_, i) => i !== idx));
  }

  function toggleBiweekly(next: boolean) {
    setBiweekly(next);
    // Leaving biweekly collapses every row back to "every week" — including
    // rows in location tabs that aren't currently on screen.
    if (!next) setRules(rules.map((r) => ({ ...r, week: 0 })));
  }

  /**
   * Switching modes rewrites location_id on every row rather than just
   * filtering the view. The slot engine UNIONS all applicable rules
   * (inStaffAvailability in src/lib/availability.ts), so a leftover
   * "any location" row would silently widen every office back to the global
   * schedule — a location row can add time but never narrow.
   */
  function togglePerLocation(next: boolean) {
    const assigned = new Set(locations.map((l) => l.id));
    const orphans = rules.filter((r) => r.location_id !== null && !assigned.has(r.location_id));

    if (next) {
      // Expand: each "any location" row becomes one row per location. Lossless.
      const seed = rules.filter((r) => r.location_id === null);
      const pinned = rules.filter((r) => r.location_id !== null && assigned.has(r.location_id));
      const expanded = locations.flatMap((l) =>
        seed.map((r) => ({ ...r, id: undefined, location_id: l.id })),
      );
      setRules([...orphans, ...pinned, ...expanded]);
      setPerLocation(true);
      return;
    }

    // Collapse: only one schedule can survive, so confirm which one first.
    const kept = activeLoc ? rules.filter((r) => r.location_id === activeLoc.id) : [];
    const discarded = locations.filter((l) => l.id !== activeLoc?.id).map((l) => l.name);
    if (
      discarded.length &&
      !window.confirm(
        `Use your ${activeLoc?.name} hours at every location?\n\n` +
          `Your separate hours for ${discarded.join(', ')} will be discarded.`,
      )
    )
      return;
    setRules([...orphans, ...kept.map((r) => ({ ...r, id: undefined, location_id: null }))]);
    setPerLocation(false);
  }

  /** Replace the active tab's rows with a copy of another location's. */
  function copyFrom(sourceId: string) {
    if (!activeLocId || !sourceId || sourceId === activeLocId) return;
    const source = rules.filter((r) => r.location_id === sourceId);
    const existing = rules.filter((r) => r.location_id === activeLocId);
    const sourceName = locations.find((l) => l.id === sourceId)?.name;
    if (
      existing.length &&
      !window.confirm(`Replace your ${activeLoc?.name} hours with a copy of ${sourceName}?`)
    )
      return;
    setRules([
      ...rules.filter((r) => r.location_id !== activeLocId),
      ...source.map((r) => ({ ...r, id: undefined, location_id: activeLocId })),
    ]);
  }

  /**
   * Rows in a location tab you aren't looking at can still fail the server's
   * per-rule checks, so name the offending day and office rather than letting
   * the save return an unattributable error.
   */
  function validationError(): string | null {
    for (const r of rules) {
      if (r.start_time >= r.end_time) {
        const where = r.location_id
          ? ` at ${locations.find((l) => l.id === r.location_id)?.name ?? 'another location'}`
          : '';
        return `${DAY_NAMES[r.day_of_week]}${where}: start time must be before end time.`;
      }
    }
    if (rules.length > MAX_RULES) {
      return `Too many rows (${rules.length}). The limit is ${MAX_RULES} across all locations.`;
    }
    return null;
  }

  async function save() {
    if (!staffId) return;
    const invalid = validationError();
    if (invalid) {
      setErr(invalid);
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await apiFn(`/staff/${staffId}/availability`, {
        method: 'PUT',
        body: { rules: rules.map(({ id: _id, ...r }) => r) },
      });
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // A load failure leaves nothing to edit; later errors render inline so the
  // unsaved schedule on screen survives them.
  if (err && !staffId) return <p className="err">{err}</p>;
  if (!staffId) return <p className="muted">Loading…</p>;

  const now = new Date();
  const currentLabel = weekParity(now) === 0 ? 'Week A' : 'Week B';

  return (
    <div>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>
          <Clock size={16} /> Weekly availability
        </h2>
        <p className="muted">
          Times are in your personal timezone. Add multiple rows on the same day for split shifts.
        </p>

        {locations.length === 0 && (
          <p className="warn-text">
            You aren't assigned to any location yet, so you can't be booked. Ask an admin to assign
            you to one.
          </p>
        )}

        {locations.length > 1 && (
          <label className="switch-row" style={{ margin: '0 0 0.5rem' }}>
            <input
              type="checkbox"
              role="switch"
              className="switch-input"
              checked={perLocation}
              onChange={(e) => togglePerLocation(e.target.checked)}
            />
            <span className="switch" aria-hidden="true" />
            <span>Set different hours for each location</span>
          </label>
        )}
        {locations.length > 1 && !perLocation && (
          <p className="field-help" style={{ marginTop: 0 }}>
            These hours apply at all {locations.length} locations you work at.
          </p>
        )}

        <label className="switch-row" style={{ margin: '0 0 0.5rem' }}>
          <input
            type="checkbox"
            role="switch"
            className="switch-input"
            checked={biweekly}
            onChange={(e) => toggleBiweekly(e.target.checked)}
          />
          <span className="switch" aria-hidden="true" />
          <span>Use a 2-week (A / B) rotation</span>
        </label>
        {biweekly && (
          <p className="field-help" style={{ marginTop: 0 }}>
            Tag each row with the week it applies to (or leave it on “Every week”).
            The rotation flips every Sunday — <strong>this week ({weekRangeLabel(now)}) is {currentLabel}</strong>,
            next week is {currentLabel === 'Week A' ? 'Week B' : 'Week A'}.
          </p>
        )}

        {perLocation && (
          <>
            <nav className="tabs" style={{ margin: '1rem 0 0.75rem' }} aria-label="Locations">
              {locations.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className={`tab${l.id === activeLocId ? ' active' : ''}`}
                  aria-current={l.id === activeLocId ? 'true' : undefined}
                  onClick={() => setActiveLocId(l.id)}
                >
                  <MapPin size={14} /> {l.name}
                </button>
              ))}
            </nav>
            {activeLoc && (
              <p className="field-help" style={{ marginTop: 0 }}>
                {summarizeHours(activeLoc)}
              </p>
            )}
            {activeLoc && activeLoc.timezone !== staffTz && (
              <p className="field-help" style={{ marginTop: 0 }}>
                This office is in {activeLoc.timezone}, but the times below stay in your own
                timezone ({staffTz}).
              </p>
            )}
            {activeLoc && visibleIdx.length === 0 && (
              <p className="warn-text">
                You have no hours at {activeLoc.name}, so you can't be booked there. Add rows below,
                or copy your hours from another location.
              </p>
            )}
          </>
        )}

        <table>
          <thead>
            <tr>
              <th>Day</th>
              {biweekly && <th>Week</th>}
              <th>Start</th>
              <th>End</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleIdx.map((idx) => {
              const r = rules[idx]!;
              return (
                <tr key={idx}>
                  <td>
                    <select
                      value={r.day_of_week}
                      onChange={(e) => updateRule(idx, { day_of_week: parseInt(e.target.value, 10) })}
                    >
                      {DAY_NAMES.map((n, dow) => (
                        <option key={n} value={dow}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </td>
                  {biweekly && (
                    <td>
                      <select
                        value={r.week}
                        onChange={(e) => updateRule(idx, { week: parseInt(e.target.value, 10) })}
                      >
                        {WEEK_OPTIONS.map((w) => (
                          <option key={w.value} value={w.value}>
                            {w.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                  <td>
                    <input
                      type="time"
                      value={r.start_time}
                      onChange={(e) => updateRule(idx, { start_time: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={r.end_time}
                      onChange={(e) => updateRule(idx, { end_time: e.target.value })}
                    />
                  </td>
                  <td>
                    <button className="danger" onClick={() => removeRule(idx)}>
                      <Trash2 size={14} /> Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="row" style={{ marginTop: '0.75rem' }}>
          <button onClick={addRule}>
            <Plus size={14} /> Add row
          </button>
          {perLocation && locations.length > 1 && (
            <select value="" onChange={(e) => copyFrom(e.target.value)}>
              <option value="">Copy hours from…</option>
              {locations
                .filter((l) => l.id !== activeLocId)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
            </select>
          )}
          <div className="spacer" />
          {savedAt && <span className="muted">Saved {savedAt}</span>}
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        {err && (
          <p className="err" style={{ marginBottom: 0 }}>
            {err}
          </p>
        )}
      </div>
    </div>
  );
}
